import { ChangeEvent, useCallback, useRef } from 'react';

import { TFunction, useTranslation } from '@mint-lib/i18n';
import { useProfile } from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';
import { useNavigate } from '@mint/routing';
import { useDebouncedCallback } from 'use-debounce';

import { SearchBar, Typography } from '@myn/mui';

import useConditionalSearch from '../../hooks/useConditionalSearch/useConditionalSearch.js';
import useTab, { RouteTabs } from '../../hooks/useTab/useTab.js';
import AddButton from '../../molecules/AddButton/AddButton.jsx';
import Tabs from '../../molecules/Tabs/Tabs.jsx';

export const CreateModalLabelMap: (
  t: TFunction,
) => Record<RouteTabs, string> = (t: TFunction) => ({
  [RouteTabs.User]: t('New user'),
  [RouteTabs.Role]: t('New role'),
});

const Filters = () => {
  const { t } = useTranslation('@myn/permissions');

  const { profileService } = useProfile();

  const navigate = useNavigate();

  const { search } = useConditionalSearch();

  const ref = useRef<HTMLInputElement | null>(null);

  const activeRoute = useTab();

  const isAgencyCompany = profileService.isAgencyCompany();

  const ff = useFlags('@mint/arm', ['usersManagementAgGrid']);
  // To keep on showing the search + New user for the v1 table.
  const isUserAgGridView =
    activeRoute === RouteTabs.User && ff.usersManagementAgGrid.enabled;

  const handleChange = useCallback(
    (searchQuery?: string) => {
      navigate({
        // @ts-expect-error TS2322 TODO: use routeApi navigate
        search: () => ({ search: searchQuery }),
      });
    },
    [navigate],
  );

  const onChangeDebounced = useDebouncedCallback(handleChange, 300);

  return (
    <div className="mr-0.5 flex flex-col gap-4">
      <div className="flex min-h-10 items-center justify-between">
        <Typography variant="h3">{t('Users Management')}</Typography>
        {(isAgencyCompany && activeRoute === 'role') ||
        isUserAgGridView ? null : (
          <div className="mr-0.5 flex items-center gap-4">
            <SearchBar
              innerRef={ref}
              size="medium"
              defaultValue={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                onChangeDebounced(e.target.value || undefined);
              }}
              onClear={() => {
                handleChange(undefined);

                if (ref.current) {
                  ref.current.value = '';
                }
              }}
              placeholder={`${t('Search')}`}
              sx={{ width: '300px' }}
              aria-label="search bar"
            />
            <AddButton />
          </div>
        )}
      </div>
      <Tabs />
    </div>
  );
};

export default Filters;
